import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';

export interface Vessel {
  id?: string;  // UUID
  name: string;
  type: string;  // VesselType enum - REQUIRED
  vesselIdentificationNumber?: string;  // Registration/identification number
  /** Maritime Mobile Service Identity: exactly 9 decimal digits when set (optional). */
  mmsi?: string;
  vesselClass?: string;  // VesselClass enum: RECREATIONAL_CRAFT, COMMERCIAL_VESSEL, etc.
  subtype?: string;
  category?: string;  // Category enum: A_OCEAN, B_OFFSHORE, C_INSHORE, D_SHELTERED
  flag?: string;
  length?: number;
  beam?: number;
  draught?: number;
  permittedEnginePower?: number;
  firstRegistrationDate?: string;  // ISO date
  yearOfManufacture?: number;
  registrationCertificate?: string;
  registrationCertificateIssueDate?: string;  // ISO date
  marinaId?: string;
  /** Denormalized from API when present. */
  marinaName?: string;
  captainId?: string;  // Captain (private person) ID
  captainName?: string;  // Captain name (read-only from API)
  customerId?: string;  // Customer/owner ID
  notes?: string;  // Additional notes
  
  // Additional fields from API
  sailArea?: number;
  displacement?: number;
  maxNumberOfPersons?: number;
  numberOfMotors?: number;
  motorType?: string;  // DIESEL, GASOLINE, ELECTRIC, etc.
  areaOfUse?: string;
  ceMark?: string;
  sailNumber?: string;
  hullMaterial?: string;  // PLASTIC, FIBERGLASS, STEEL, etc.
  carryingCapacity?: number;
  lightweight?: number;
  grossTonnage?: number;
  countryOfOrigin?: string;
  
  // UI helper fields (not sent to API)
  searchLabel?: string;
  stringId?: string;
}

/** Latest AIS position from GET .../vessels/mmsi/{mmsi}/ais/latest-position (OpenAPI VesselAisLocationDto). */
export interface VesselAisLocationDto {
  mmsi?: string;
  longitude?: number;
  latitude?: number;
  /** Time of the AIS position report (ISO date-time). */
  positionTime?: string;
  speedOverGroundKnots?: number;
  courseOverGroundDegrees?: number;
  vesselName?: string;
  trackPointCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class VesselService {
  private apiUrl = '/api/v1/vessels';

  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService
  ) {
    console.log('[VesselService] Service initialized with WebSocket support');
  }
  
  /**
   * Get observable for real-time vessel updates from WebSocket (create/update)
   * Vessels use a global topic without client ID
   */
  getRealtimeVesselUpdates(): Observable<any> {
    return this.webSocketService.subscribe('/topic/vessels');
  }
  
  /**
   * Get observable for real-time vessel deletions from WebSocket
   * Vessels use a global topic without client ID
   */
  getRealtimeVesselDeletions(): Observable<any> {
    return this.webSocketService.subscribe('/topic/vessels');
  }

  /**
   * Get all vessels
   */
  getVessels(): Observable<Vessel[]> {
    const headers = this.getAuthHeaders();
    
    console.log('Fetching vessels from:', this.apiUrl);

    return this.http.get<Vessel[]>(this.apiUrl, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching vessels:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get vessel by ID
   */
  getVesselById(id: number | string): Observable<Vessel> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<Vessel>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching vessel by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Latest AIS position (BarentsWatch historic track) for a 9-digit MMSI.
   */
  getAisLatestPosition(mmsi: string): Observable<VesselAisLocationDto> {
    const headers = this.getAuthHeaders();
    const pathMmsi = encodeURIComponent(mmsi.trim());
    return this.http
      .get<VesselAisLocationDto>(`${this.apiUrl}/mmsi/${pathMmsi}/ais/latest-position`, { headers })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching AIS latest position:', error);
          this.handleAuthError(error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Create a new vessel
   */
  createVessel(vessel: Omit<Vessel, 'id'>): Observable<Vessel> {
    const headers = this.getAuthHeaders();
    
    console.log('Creating vessel:', vessel);

    return this.http.post<Vessel>(this.apiUrl, vessel, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating vessel:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing vessel
   */
  updateVessel(id: string, vessel: Partial<Vessel>): Observable<Vessel> {
    const headers = this.getAuthHeaders();
    
    console.log('Updating vessel:', id, vessel);

    return this.http.put<Vessel>(`${this.apiUrl}/${id}`, vessel, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating vessel:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a vessel
   */
  deleteVessel(id: string): Observable<void> {
    const headers = this.getAuthHeaders();
    
    console.log('Deleting vessel:', id);

    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting vessel:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }
}

